import type { NotificationChannel } from './notificationMessage';

export type TemplateLanguageCode = 'KO_KR' | 'EN_US' | 'JA_JP' | 'ZH_CN';

/**
 * 카카오 알림톡 유형(알림톡 전용; SMS/이메일은 null). FE 내부값은 소문자, BE wire 는 대문자
 * (BASIC|EMPHASIS|IMAGE) — hook normalize 에서 변환. 라벨 SSoT = i18n template.kakaoType.*
 */
export type KakaoTemplateType = 'basic' | 'emphasis' | 'image';

/** 템플릿 검수 상태. FE 소문자 / BE 대문자(DRAFT|PENDING|APPROVED|REJECTED). 라벨 SSoT = template.approval.* */
export type TemplateApprovalStatus = 'draft' | 'pending' | 'approved' | 'rejected';

/** 템플릿 활성 상태(BE ACTIVE|INACTIVE). isActive 대체. */
export type TemplateStatus = 'ACTIVE' | 'INACTIVE';

/** 카카오 버튼 타입(BE TemplateButtonView.type): 웹링크/앱링크/배송조회/봇키워드/메시지전달. */
export type KakaoButtonType = 'WL' | 'AL' | 'DS' | 'BK' | 'MD';

/** 템플릿 버튼(BE TemplateButtonView 정합) — buttonsJson 문자열 아님, 구조화 배열. */
export interface TemplateButton {
  name: string;
  type: KakaoButtonType;
  urlMobile: string | null;
  urlPc: string | null;
}

export interface TemplateListItem {
  templateId: string;
  templateCode: string;
  channel: NotificationChannel;
  languageCode: TemplateLanguageCode;
  subject: string | null;
  /** 정합 추가(§3.1): 알림톡 유형. EMAIL/SMS 는 null. */
  kakaoTemplateType: KakaoTemplateType | null;
  approvalStatus: TemplateApprovalStatus | null;
  isAdvertising: boolean;
  /** ID-226 — EMAIL HTML 본문 여부(wire `isHtml`, light view 추가). 목록 배지용. default false. */
  isHtml: boolean;
  createdAt: string;
}

export interface TemplateListPage {
  items: TemplateListItem[];
  total: number;
  page: number;
  size: number;
}

export interface TemplateDetail {
  templateId: string;
  templateCode: string;
  channel: NotificationChannel;
  languageCode: TemplateLanguageCode;
  subject: string | null;
  body: string;
  variablesJson: string | null;
  /** BE status(ACTIVE|INACTIVE). 활성 여부 = status==='ACTIVE'. */
  status: TemplateStatus;
  isAdvertising: boolean;
  createdAt: string;
  updatedAt: string;
  // ── 정합 추가(§3.1) — 알림톡 패리티. EMAIL/SMS 는 null/false. ──
  kakaoTemplateType: KakaoTemplateType | null;
  emphasisTitle: string | null;
  emphasisSubtitle: string | null;
  /** 이미지형 대표 이미지 URL(BE imageUrl, nullable). */
  imageUrl: string | null;
  /** 카카오 표준 버튼(구조화 배열 — BE TemplateButtonView). */
  buttons: TemplateButton[];
  reprLinkMobile: string | null;
  reprLinkPc: string | null;
  approvalStatus: TemplateApprovalStatus | null;
  autoSendEnabled: boolean;
  /** 비즈톡 검수 승인 템플릿 코드(tmpltCode). 발송 시 본문 일치 필요. */
  providerTemplateCode: string | null;
  /**
   * ID-226 — EMAIL HTML 본문 발송 플래그(BE NotificationTemplate.is_html, wire `isHtml`).
   * true 면 본문을 HTML 로 발송(SES Body.Html.Data)·미리보기를 HTML 렌더. default false=기존 plain.
   */
  isHtml: boolean;
}

export interface TemplateCreatePayload {
  templateCode: string;
  channel: NotificationChannel;
  languageCode: TemplateLanguageCode;
  subject?: string | null;
  body: string;
  variablesJson?: string | null;
  kakaoTemplateType?: KakaoTemplateType | null;
  emphasisTitle?: string | null;
  emphasisSubtitle?: string | null;
  imageUrl?: string | null;
  buttons?: TemplateButton[];
  reprLinkMobile?: string | null;
  reprLinkPc?: string | null;
  providerTemplateCode?: string | null;
  autoSendEnabled?: boolean;
  isAdvertising?: boolean;
  /** ID-226 — EMAIL HTML 발송 플래그(wire `isHtml`). EMAIL 외 채널은 무의미(BE 무시). */
  isHtml?: boolean;
}

export interface TemplateUpdatePayload {
  subject?: string | null;
  body: string;
  variablesJson?: string | null;
  isAdvertising?: boolean;
  kakaoTemplateType?: KakaoTemplateType | null;
  emphasisTitle?: string | null;
  emphasisSubtitle?: string | null;
  imageUrl?: string | null;
  buttons?: TemplateButton[];
  reprLinkMobile?: string | null;
  reprLinkPc?: string | null;
  approvalStatus?: TemplateApprovalStatus | null;
  providerTemplateCode?: string | null;
  autoSendEnabled?: boolean;
  /** ID-226 — EMAIL HTML 발송 플래그(wire `isHtml`). EMAIL 외 채널은 무의미(BE 무시). */
  isHtml?: boolean;
}

export interface TemplateListFilter {
  q?: string;
  channel?: NotificationChannel;
  languageCode?: TemplateLanguageCode;
  kakaoTemplateType?: KakaoTemplateType;
  approvalStatus?: TemplateApprovalStatus;
  page: number;
  size: number;
}

export const TEMPLATE_CHANNELS: ReadonlyArray<NotificationChannel> = [
  'EMAIL',
  'SMS',
  'KAKAO_BIZ',
];

export const TEMPLATE_LANGUAGES: ReadonlyArray<TemplateLanguageCode> = [
  'KO_KR',
  'EN_US',
  'JA_JP',
  'ZH_CN',
];
